import type { ProductOwnerCadence, ProductOwnerRun } from '../stores/product_owner_runs'
import { productOwnerCadenceStateExitClause } from './productOwnerCadenceState'
import { formatDate } from './useFormattedDate'

// The cadence and next-check phrases on a ProductOwnerRunCard.vue card.
// Split out of the card (which hit the 300-line limit when the total
// backlog line landed) as pure functions of the run's fields plus the
// caller's `now`, so the card keeps only its own state and layout.

// "Next check" (#1344) only appears on the Latest-per-repo cards (the
// backend only attaches it there — see Agent::ProductOwnerRunsController).
// A bare future timestamp would be misleading for most states (no run is
// actually scheduled for a disabled/paused repo, and a currently-running
// repo isn't waiting on a timestamp at all), so each state gets its own
// copy instead of always rendering a time. 'held' (#1744) is the one other
// state with a time: when the account-wide Claude session limit hold lifts.
export function formatNextCheck(nextCheck: ProductOwnerRun['next_check'], now: Date): string {
  if (!nextCheck) return '—'
  if (nextCheck.state === 'disabled') return '—'
  if (nextCheck.state === 'running') return 'Running now'
  if (nextCheck.state === 'overrun') return 'When this run finishes (overrunning its interval)'
  if (nextCheck.state === 'paused') return 'Paused (circuit breaker)'
  if (nextCheck.state === 'held') {
    return nextCheck.at
      ? `Held until ${formatDate(nextCheck.at, now)} (Claude session limit)`
      : 'Held (Claude session limit)'
  }
  if (!nextCheck.at) return '—'

  const minutes = Math.max(0, Math.round((new Date(nextCheck.at).getTime() - now.getTime()) / 60_000))
  return `in ~${minutes}m (${formatDate(nextCheck.at, now)})`
}

// Only latest_per_repo rows carry cadence. The arrow appears only when the
// next recompute would actually move the interval.
export function formatCadence(cadence: ProductOwnerRun['cadence']): string {
  if (!cadence) return '—'
  const base = `${cadence.current_interval_minutes}m`
  if (
    cadence.resulting_interval_minutes == null ||
    cadence.resulting_interval_minutes === cadence.current_interval_minutes
  ) {
    return base
  }
  return `${base} → ${cadence.resulting_interval_minutes}m`
}

export function cadenceTitle(cadence: ProductOwnerRun['cadence']): string | undefined {
  if (!cadence) return undefined
  const parts = [
    `Today: ${cadence.today_count ?? '—'}`,
    `Baseline: ${cadence.baseline_count ?? '—'}`,
    `Max: ${cadence.max_interval_minutes}m`,
  ]
  return parts.join(' · ')
}

// The cadence algorithm's own persisted label (#1536), rendered as a pill
// beside the run-status pill. The tooltip names the state's own exit
// condition (#1625) and which mechanism last moved the interval and when,
// so a surprising state is traceable without leaving the card.
export function cadenceStateTitle(cadence: ProductOwnerCadence, now: Date): string {
  const base =
    "The cadence algorithm's own read-only label: growing/flat/shrinking from the daily trend, " +
    'bursting from a live spike, or idle after several no-progress runs.'
  const exit = productOwnerCadenceStateExitClause(cadence.state)
  const withExit = exit ? `${base} ${exit}` : base
  if (!cadence.last_adjusted_by) return withExit
  const at = cadence.last_adjusted_at ? ` at ${formatDate(cadence.last_adjusted_at, now)}` : ''
  return `${withExit} Last adjusted by ${cadence.last_adjusted_by}${at}.`
}
